<template>
  <el-button
    class="base-button"
    :class="[
      `base-button--${variant}`,
      `base-button--${normalizedSize}`,
      { 'is-block': block }
    ]"
    :type="buttonType"
    :size="normalizedSize"
    :loading="loading"
    :disabled="disabled"
    :plain="isPlain"
    :text="isText"
    :bg="isText"
    :native-type="nativeType"
    @click="$emit('click', $event)"
  >
    <slot />
  </el-button>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'primary'
  },
  size: {
    type: String,
    default: 'default'
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  block: {
    type: Boolean,
    default: false
  },
  nativeType: {
    type: String,
    default: 'button'
  }
})

defineEmits(['click'])

const normalizedSize = computed(() => {
  if (props.size === 'large' || props.size === 'small' || props.size === 'default') {
    return props.size
  }
  return 'default'
})

const buttonType = computed(() => {
  if (props.variant === 'primary') return 'primary'
  if (props.variant === 'danger') return 'danger'
  if (props.variant === 'warning') return 'warning'
  if (props.variant === 'text') return 'primary'
  return 'default'
})

const isPlain = computed(() => props.variant === 'outline')
const isText = computed(() => props.variant === 'text')
</script>

<style scoped lang="scss">
.base-button {
  border-radius: var(--radius-md);
  font-weight: 500;
  letter-spacing: 0;
  transition:
    transform var(--motion-fast) var(--ease-standard),
    box-shadow var(--motion-fast) var(--ease-standard),
    background-color var(--motion-fast) var(--ease-standard),
    border-color var(--motion-fast) var(--ease-standard),
    color var(--motion-fast) var(--ease-standard);

  &:not(.is-disabled):active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}

.base-button--default {
  min-height: var(--control-height);
}

.base-button--small {
  min-height: var(--control-height-sm);
}

.base-button--large {
  min-height: var(--control-height-lg);
}

.base-button--primary {
  --el-button-bg-color: var(--color-primary);
  --el-button-border-color: var(--color-primary);
  --el-button-hover-bg-color: var(--color-primary-hover);
  --el-button-hover-border-color: var(--color-primary-hover);
  --el-button-active-bg-color: var(--color-primary-active);
  --el-button-active-border-color: var(--color-primary-active);
}

.base-button--outline {
  --el-button-text-color: var(--color-primary);
  --el-button-border-color: var(--color-primary);
  --el-button-hover-text-color: var(--color-primary);
  --el-button-hover-bg-color: var(--color-primary-soft);
  --el-button-hover-border-color: var(--color-primary);
  --el-button-active-bg-color: var(--color-primary-soft);
  --el-button-active-text-color: var(--color-primary-active);
}

.base-button--secondary {
  --el-button-text-color: var(--color-text-primary);
  --el-button-bg-color: var(--color-surface);
  --el-button-border-color: var(--color-border);
  --el-button-hover-text-color: var(--color-primary);
  --el-button-hover-bg-color: var(--color-primary-soft);
  --el-button-hover-border-color: var(--color-primary);
  --el-button-active-bg-color: var(--color-primary-soft);
  --el-button-active-border-color: var(--color-primary-active);
}

.base-button--text {
  --el-button-text-color: var(--color-primary);
  --el-button-hover-text-color: var(--color-primary-hover);
  --el-button-active-text-color: var(--color-primary-active);
}

.base-button--danger {
  --el-button-bg-color: var(--color-danger);
  --el-button-border-color: var(--color-danger);
}

.base-button--warning {
  --el-button-bg-color: var(--color-warning);
  --el-button-border-color: var(--color-warning);
}

.is-block {
  width: 100%;
}

@media (max-width: 768px) {
  .base-button:not(.base-button--small) {
    min-height: 44px;
  }
}
</style>
